import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

class EventService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/events`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar errores
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expirado o inválido
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminData');
          window.location.href = '/admin';
        }
        return Promise.reject(error);
      }
    );
  }

  // Obtener todos los eventos
  async getEvents() {
    try {
      const response = await this.api.get('/');
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener eventos');
      throw new Error(errorMessage);
    }
  }

  // Obtener eventos activos
  async getActiveEvents() {
    try {
      const response = await this.api.get('/active');
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener eventos activos');
      throw new Error(errorMessage);
    }
  }

  // Obtener evento por ID
  async getEventById(id) {
    try {
      const response = await this.api.get(`/${id}`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener evento');
      throw new Error(errorMessage);
    }
  }

  // Buscar eventos por título
  async searchEventsByTitle(title) {
    try {
      const response = await this.api.get(`/search?title=${encodeURIComponent(title)}`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al buscar eventos');
      throw new Error(errorMessage);
    }
  }

  // Obtener eventos por rango de fechas
  async getEventsByDateRange(startDate, endDate) {
    try {
      const response = await this.api.get(`/date-range?startDate=${startDate}&endDate=${endDate}`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener eventos por fecha');
      throw new Error(errorMessage);
    }
  }

  // Obtener estadísticas de un evento
  async getEventStats(id) {
    try {
      const response = await this.api.get(`/${id}/stats`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener estadísticas del evento');
      throw new Error(errorMessage);
    }
  }

  // Crear nuevo evento
  async createEvent(eventData) {
    try {
      const response = await this.api.post('/', eventData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al crear evento');
      throw new Error(errorMessage);
    }
  }

  // Actualizar evento
  async updateEvent(id, eventData) {
    try {
      const response = await this.api.patch(`/${id}`, eventData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al actualizar evento');
      throw new Error(errorMessage);
    }
  }

  // Actualizar estado del evento
  async updateEventStatus(id, status) {
    try {
      const response = await this.api.patch(`/${id}/status?status=${status}`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al actualizar estado del evento');
      throw new Error(errorMessage);
    }
  }

  // Eliminar evento
  async deleteEvent(id) {
    try {
      await this.api.delete(`/${id}`);
      return true;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al eliminar evento');
      throw new Error(errorMessage);
    }
  }

  // Método para obtener mensajes de error amigables
  getErrorMessage(error, defaultMessage) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    // Manejar errores específicos de red
    if (error.code === 'NETWORK_ERROR') {
      return 'Error de conexión. Verifica tu conexión a internet.';
    }
    
    if (error.response?.status === 409) {
      return 'Conflicto: Los datos proporcionados ya existen en el sistema.';
    }
    
    if (error.response?.status === 400) {
      return 'Datos inválidos. Por favor, verifica la información ingresada.';
    }
    
    if (error.response?.status === 404) {
      return 'Evento no encontrado.';
    }
    
    if (error.response?.status === 500) {
      return 'Error interno del servidor. Por favor, intenta nuevamente.';
    }
    
    return defaultMessage;
  }
}

const eventService = new EventService();
export default eventService; 